import { Component, Inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';

import { MessstelleDuplikatErgebnis } from '../../../../../Models/messstelle.model';

export interface MessstelleDuplizierenDialogData {
  /** Bisherige Kennzeichnung – nur zur Orientierung, wird nicht vorbelegt. */
  alteKennzeichnung: string;
  /** Wie die Kennzeichnung in dieser Liste heißt: „KKS“, „AKZ“, … */
  kennzeichnungLabel: string;
  zeile: number;
  /** Wie viele Prüfpunkte mitkommen – für den Hinweistext. */
  pruefpunkte: number;
  /** Bereits vergebene Kennzeichnungen der Liste, mit `vergleichbar` normalisiert. */
  vergeben: ReadonlySet<string>;
  /** Dieselbe Normalisierung wie beim Anlegen: Groß-/Kleinschreibung und Leerraum egal. */
  vergleichbar: (wert: string) => string;
  /**
   * Führt die Duplizierung aus. Der Dialog bleibt bei einem Fehler offen und
   * zeigt die Meldung am Feld – geschlossen wird erst nach Erfolg.
   */
  ausfuehren: (kennzeichnung: string) => Observable<MessstelleDuplikatErgebnis>;
}

/**
 * Dialog „Messstelle duplizieren“: fragt nur die neue Kennzeichnung ab.
 *
 * Alles andere – Werte, Kategorie, Anlagenteil, Prüfpunkte mit Ergebnissen,
 * Verlauf, Fotos, Dateien – übernimmt der Server 1:1 vom Original. Die
 * Eindeutigkeit wird zweimal geprüft: hier gegen die geladene Liste, damit
 * der Fehler sofort am Feld steht, und noch einmal serverseitig mit
 * derselben Regel gegen den aktuellen Stand.
 */
@Component({
  selector: 'app-messstelle-duplizieren-dialog',
  template: `
    <h2 mat-dialog-title>Messstelle duplizieren</h2>

    <mat-dialog-content>

      <p class="hinweis">
        Die Messstelle wird mit allen vorhandenen Daten und Prüfergebnissen kopiert
        <ng-container *ngIf="data.pruefpunkte > 0">
          ({{ data.pruefpunkte }} Prüfpunkt{{ data.pruefpunkte === 1 ? '' : 'e' }} samt Ergebnissen)</ng-container>.
        Bitte gib eine neue eindeutige Kennzeichnung ein.
      </p>

      <p class="original">
        <span class="original-label">Bisher:</span>
        <span class="original-wert" *ngIf="data.alteKennzeichnung">{{ data.alteKennzeichnung }}</span>
        <span class="original-wert original-leer" *ngIf="!data.alteKennzeichnung">Zeile {{ data.zeile }} ohne {{ data.kennzeichnungLabel }}</span>
      </p>

      <mat-form-field appearance="outline" class="feld" [class.feld-fehler]="!!anzeigeFehler">
        <mat-label>Neue {{ data.kennzeichnungLabel }} / Kennzeichnung</mat-label>
        <input matInput cdkFocusInitial type="text" [(ngModel)]="kennzeichnung" (ngModelChange)="onEingabe()"
          [disabled]="laeuft" maxlength="400" required autocomplete="off" spellcheck="false"
          [attr.aria-invalid]="anzeigeFehler ? 'true' : null" aria-describedby="duplizieren-fehler"
          (keydown.enter)="duplizieren()" [placeholder]="platzhalter">
        <mat-hint *ngIf="!anzeigeFehler">Muss in dieser Liste eindeutig sein.</mat-hint>
      </mat-form-field>

      <p class="feld-fehler-text" id="duplizieren-fehler" role="alert" *ngIf="anzeigeFehler">
        <mat-icon>error_outline</mat-icon>{{ anzeigeFehler }}
      </p>

    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="abbrechen()" [disabled]="laeuft">Abbrechen</button>
      <button mat-raised-button color="primary" type="button" (click)="duplizieren()"
        [disabled]="laeuft || !kennzeichnung.trim()">
        <mat-spinner diameter="18" *ngIf="laeuft"></mat-spinner>
        <mat-icon *ngIf="!laeuft">content_copy</mat-icon>
        Duplizieren
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content { width: 460px; max-width: 100%; }
    .hinweis { margin: 0 0 var(--space-3); font-size: var(--fs-base); line-height: 1.5; color: var(--text-secondary); }
    .original {
      display: flex; align-items: baseline; gap: var(--space-2); flex-wrap: wrap;
      margin: 0 0 var(--space-3); font-size: var(--fs-sm); color: var(--text-secondary);
    }
    .original-wert { font-family: var(--font-mono); font-size: var(--fs-md); color: var(--text-primary); }
    .original-leer { font-family: var(--font-sans); font-style: italic; color: var(--text-tertiary); }
    .feld { width: 100%; }
    .feld-fehler-text {
      display: flex; align-items: center; gap: 4px;
      margin: -14px 0 0; font-size: var(--fs-xs); color: var(--warn);
    }
    .feld-fehler-text .mat-icon { font-size: 16px; width: 16px; height: 16px; }
    .feld-fehler ::ng-deep .mdc-notched-outline__leading,
    .feld-fehler ::ng-deep .mdc-notched-outline__notch,
    .feld-fehler ::ng-deep .mdc-notched-outline__trailing { border-color: var(--warn) !important; }
    mat-dialog-actions mat-spinner { display: inline-block; margin-right: 6px; }
  `]
})
export class MessstelleDuplizierenDialogComponent {

  kennzeichnung = '';

  laeuft = false;

  /** Meldung vom Server oder aus der Vorprüfung – steht direkt am Feld. */
  fehler = '';

  /** Erst nach dem ersten Versuch meckern, nicht beim Tippen. */
  private geprueft = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MessstelleDuplizierenDialogData,
    private dialogRef: MatDialogRef<MessstelleDuplizierenDialogComponent, MessstelleDuplikatErgebnis | null>
  ) { }

  get platzhalter(): string {
    return this.data.alteKennzeichnung
      ? `z. B. ${this.data.alteKennzeichnung}-KOPIE`
      : '';
  }

  /** Lokale Prüfung mit denselben Regeln wie beim Anlegen einer Messstelle. */
  get lokalerFehler(): string {
    const wert = this.kennzeichnung.trim();

    if (!wert) {
      return 'Bitte eine Kennzeichnung eingeben.';
    }

    if (this.data.vergeben.has(this.data.vergleichbar(wert))) {
      return `„${wert}“ ist in dieser Liste bereits vergeben.`;
    }

    return '';
  }

  get anzeigeFehler(): string {
    if (this.fehler) {
      return this.fehler;
    }

    // Die Dublettenprüfung sofort, die Pflichtprüfung erst nach dem ersten Versuch.
    const lokal = this.lokalerFehler;
    if (lokal && (this.geprueft || this.kennzeichnung.trim())) {
      return lokal;
    }

    return '';
  }

  onEingabe(): void {
    // Eine Servermeldung gilt für den Wert, zu dem sie kam.
    this.fehler = '';
  }

  duplizieren(): void {
    if (this.laeuft) {
      return;
    }

    this.geprueft = true;

    if (this.lokalerFehler) {
      return;
    }

    const wert = this.kennzeichnung.trim();

    this.laeuft = true;
    this.fehler = '';
    this.dialogRef.disableClose = true;

    this.data.ausfuehren(wert).subscribe({
      next: ergebnis => {
        this.laeuft = false;
        this.dialogRef.disableClose = false;
        this.dialogRef.close(ergebnis);
      },
      error: (err: HttpErrorResponse) => {
        this.laeuft = false;
        this.dialogRef.disableClose = false;

        this.fehler =
          err?.status === 403
            ? (err?.error?.message ?? 'Für das Duplizieren fehlt die Berechtigung.')
            : (err?.error?.message
              ?? 'Die Messstelle konnte nicht dupliziert werden. Bitte erneut versuchen.');
      }
    });
  }

  abbrechen(): void {
    if (!this.laeuft) {
      this.dialogRef.close(null);
    }
  }
}
